import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api } from "../api/client";
import type { StaffSnapshot } from "../api/types";

/** Скан со сканера на мини-ПК: байты кода в Base64, как их принимает касса. */
export type ScanHandler = (code: string) => void;

interface AppStateValue {
  snapshot: StaffSnapshot | null;
  connected: boolean;
  /** Ручное обновление — на случай, если сотрудник не доверяет живому потоку. */
  refresh: () => Promise<void>;
  /**
   * Подписка на сканы. Возвращает отписку.
   *
   * Сканер висит на мини-ПК, а не на планшете, и скан приходит тем же живым
   * потоком, что и состояние. Здесь он только раздаётся: кто и что с ним
   * делает, решает экран.
   */
  subscribeScan: (handler: ScanHandler) => () => void;
}

const AppStateContext = createContext<AppStateValue | null>(null);

/**
 * Один источник рабочих данных на всё приложение.
 *
 * Сервер присылает целый снимок состояния, уже урезанный под права сотрудника,
 * поэтому экранам не нужны отдельные запросы: они просто читают нужную часть.
 */
export function AppStateProvider({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshot] = useState<StaffSnapshot | null>(null);
  const [connected, setConnected] = useState(false);
  const scanHandlers = useRef(new Set<ScanHandler>());

  useEffect(() => {
    let cancelled = false;

    api
      .get<StaffSnapshot>("/api/state")
      .then(data => {
        if (!cancelled) setSnapshot(data);
      })
      .catch(() => {});

    const stream = new EventSource("/api/events", { withCredentials: true });

    const apply = (event: MessageEvent<string>) => {
      const parsed = JSON.parse(event.data) as { payload: StaffSnapshot | null };
      if (parsed.payload) setSnapshot(parsed.payload);
      setConnected(true);
    };

    // hello приходит и при первом подключении, и после каждого автоматического
    // переподключения — это и есть восстановление данных после обрыва (п. 29 ТЗ).
    stream.addEventListener("hello", apply as EventListener);
    stream.addEventListener("state", apply as EventListener);

    // Скан — не снимок: состояние не трогает, уходит подписчикам как есть.
    stream.addEventListener("scan", ((event: MessageEvent<string>) => {
      const parsed = JSON.parse(event.data) as { payload: { code?: string } | null };
      const code = parsed.payload?.code;
      if (typeof code !== "string" || !code) return;
      for (const handler of scanHandlers.current) handler(code);
    }) as EventListener);
    stream.onopen = () => setConnected(true);
    stream.onerror = () => setConnected(false);

    return () => {
      cancelled = true;
      stream.close();
    };
  }, []);

  const value = useMemo<AppStateValue>(
    () => ({
      snapshot,
      connected,
      refresh: async () => {
        setSnapshot(await api.get<StaffSnapshot>("/api/state"));
      },
      subscribeScan: handler => {
        scanHandlers.current.add(handler);
        return () => {
          scanHandlers.current.delete(handler);
        };
      }
    }),
    [snapshot, connected]
  );

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

export function useAppState(): AppStateValue {
  const value = useContext(AppStateContext);
  if (!value) throw new Error("useAppState вызван вне AppStateProvider");
  return value;
}

/**
 * Реагировать на сканы, пока `enabled`.
 *
 * Обработчик берётся свежий на каждый скан, а не тот, что был при подписке:
 * экран кассы меняет состояние между сканами, и устаревшее замыкание
 * положило бы бутылку не в ту строку.
 */
export function useScanner(handler: ScanHandler, enabled = true): void {
  const { subscribeScan } = useAppState();
  const latest = useRef(handler);
  latest.current = handler;

  useEffect(() => {
    if (!enabled) return;
    return subscribeScan(code => latest.current(code));
  }, [enabled, subscribeScan]);
}
